import React from 'react';
import { Link } from 'react-router-dom';
import { FadeIn } from '../components/FadeIn';
const sections = [{
  number: '01',
  heading: 'What is Cognarchism?',
  paragraphs: ['Cognarchism is a political doctrine that treats judgment as a public good. It begins from the belief that modern civilization is increasingly destabilized by emotional reactivity, mass manipulation, short-termism, and institutional decline. In response, it proposes a constitutional order in which authority is not granted purely through popularity, wealth, inherited position, or political theatrics, but through demonstrated civic fitness.', 'This is not a rejection of public participation. Citizens still vote, petition, and influence policy. But Cognarchism insists that the burden of proof should rise with the level of responsibility. The more power a role carries, the higher the standard of judgment required to hold it.']
}, {
  number: '02',
  heading: 'The Core Thesis',
  paragraphs: ['Cognarchism holds that not all judgments are equal in civic consequence. In an era defined by information warfare, social fragmentation, and narrative capture, a serious political system must be able to distinguish between reactive, easily manipulated decision-making and disciplined, reality-based judgment.', 'Its central claim is that a functioning constitutional order must do more than count preferences. It must cultivate stronger citizens, build more resilient institutions, and place greater trust in those who have demonstrated competence under pressure.']
}, {
  number: '03',
  heading: 'The NCJI Framework',
  paragraphs: ['At the center of Cognarchism is the Neurocognitive Civic Judgment Index (NCJI), a framework for assessing and developing the qualities most necessary for responsible citizenship and trustworthy leadership.', 'The NCJI is built around several core domains:'],
  list: ['Consequential Thinking', 'Emotional Regulation', 'Reality Testing', 'Manipulation Resistance', 'Executive Discipline', 'Long-Term Planning', 'Social Cooperation', 'Leadership Responsibility'],
  afterList: 'The purpose of the NCJI is not only to evaluate, but to develop. It is designed as a pathway for growth, allowing citizens to strengthen their civic capacity over time rather than treating them as permanently fixed categories.'
}, {
  number: '04',
  heading: 'Citizenship as Development',
  paragraphs: ['Cognarchism replaces the idea of static citizenship with a developmental model. Under this view, citizenship is not merely a legal condition. It is an upward process of increasing responsibility, discipline, and civic readiness.', "The state does not simply sort people into winners and losers. It invests in citizens through education, recalibration, reasoning training, and civic development pathways. A person's standing is not meant to be fixed by birth, wealth, or social status, but by documented growth, self-command, and demonstrated improvement."]
}, {
  number: '05',
  heading: 'Beyond Democracy and Autocracy',
  paragraphs: ['Cognarchism presents itself as a third path beyond the weaknesses of both liberal democracy and authoritarianism.', 'It argues that liberal democracy often rewards theatricality over competence, allowing emotionally persuasive figures to gain power without demonstrating judgment. It argues that authoritarianism fails in the opposite direction, concentrating power without sufficient accountability or developmental legitimacy.', 'Cognarchism attempts to resolve both problems by combining constitutional structure, public participation, and competence-based standards. The result is a political order that seeks to preserve legitimacy while filtering high-trust authority through demonstrated fitness.']
}, {
  number: '06',
  heading: 'The Civilizational Aim',
  paragraphs: ['The long-term ambition of Cognarchism is civilizational. Its goal is not merely better administration, but a population that is more disciplined, less vulnerable to manipulation, and more capable of long-term self-government.', 'By rewarding self-mastery, competence, and responsibility, the system aims to create a cultural feedback loop in which stronger judgment becomes socially valuable and institutionally necessary. In that sense, Cognarchism is not just a model of government. It is a project of human development tied to constitutional order.']
}];
export function Overview() {
  return <article data-id="element-370">
      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-cognarch-white border-b border-cognarch-border" data-id="element-371">
        <div className="max-w-3xl mx-auto px-6" data-id="element-372">
          <FadeIn delay={0.1} direction="down" data-id="element-373">
            <p className="uppercase tracking-[0.25em] text-xs font-medium text-cognarch-gray mb-8" data-id="element-374">
              Constitutional Political Framework
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-375">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-medium leading-[1.1] tracking-tight text-cognarch-black mb-8 text-balance" data-id="element-376">
              A political order built around judgment, competence, and civic
              development
            </h1>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-377">
            <p className="text-lg md:text-xl text-cognarch-gray leading-relaxed mb-12 max-w-2xl" data-id="element-378">
              Cognarchism is a constitutional framework based on a simple
              premise: a society cannot remain stable when power is detached
              from judgment. It argues that leadership, institutional trust, and
              civic responsibility should be aligned with demonstrated
              competence, self-mastery, and long-term reasoning.
            </p>
          </FadeIn>

          <FadeIn delay={0.5} className="flex flex-col sm:flex-row gap-4" data-id="element-379">
            <Link to="/charter" className="inline-block px-7 py-3.5 bg-cognarch-black text-white font-medium tracking-wide hover:bg-cognarch-charcoal transition-all duration-300 text-sm text-center" data-id="element-380">
              Read the Charter
            </Link>
            <Link to="/ncji" className="inline-block px-7 py-3.5 border border-cognarch-charcoal text-cognarch-charcoal font-medium tracking-wide hover:bg-cognarch-charcoal hover:text-white transition-all duration-300 text-sm text-center" data-id="element-381">
              Explore the NCJI
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* Body Sections */}
      {sections.map((section, index) => <section key={section.number} className={`py-20 md:py-28 border-b border-cognarch-border ${index % 2 === 1 ? 'bg-cognarch-offwhite' : 'bg-cognarch-white'}`} data-id="element-382">
          <div className="max-w-3xl mx-auto px-6" data-id="element-383">
            <FadeIn data-id="element-384">
              <div className="flex items-baseline gap-6 mb-8" data-id="element-385">
                <span className="text-cognarch-border font-serif text-4xl md:text-5xl leading-none select-none" data-id="element-386">
                  {section.number}
                </span>
                <h2 className="text-2xl md:text-4xl font-serif text-cognarch-charcoal leading-tight" data-id="element-387">
                  {section.heading}
                </h2>
              </div>
            </FadeIn>

            <FadeIn delay={0.1} data-id="element-388">
              <div className="space-y-6" data-id="element-389">
                {section.paragraphs.map((paragraph, pIndex) => <p key={pIndex} className="text-cognarch-gray leading-[1.8] text-base md:text-lg" data-id="element-390">
                    {paragraph}
                  </p>)}

                {section.list && <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 py-4 pl-1" data-id="element-391">
                    {section.list.map((item, lIndex) => <li key={lIndex} className="flex items-center gap-3 text-cognarch-charcoal text-sm md:text-base font-medium" data-id="element-392">
                        <span className="text-cognarch-charcoal/30 text-xs" data-id="element-393">
                          ◆
                        </span>
                        {item}
                      </li>)}
                  </ul>}

                {section.afterList && <p className="text-cognarch-gray leading-[1.8] text-base md:text-lg" data-id="element-394">
                    {section.afterList}
                  </p>}
              </div>
            </FadeIn>
          </div>
        </section>)}

      {/* Closing Statement Block */}
      <section className="py-24 md:py-32 bg-cognarch-black text-white" data-id="element-395">
        <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-396">
          <FadeIn data-id="element-397">
            <div className="w-16 h-px bg-white/20 mx-auto mb-12" data-id="element-398" />
            <p className="text-xl md:text-2xl font-serif leading-relaxed text-white/80 mb-12 text-balance" data-id="element-399">
              Cognarchism is built on the belief that freedom without fitness
              decays, authority without merit corrupts, and civilization without
              self-mastery becomes unstable.
            </p>
            <div className="w-16 h-px bg-white/20 mx-auto mb-12" data-id="element-400" />
            <p className="uppercase tracking-[0.25em] text-sm text-white/50 font-medium" data-id="element-401">
              Competence before power. Judgment before rule. Development before
              decay.
            </p>
          </FadeIn>
        </div>
      </section>
    </article>;
}